/*!
VueLayers
Vue 2 components to work with OpenLayers

@package vuelayers
@author Vladimir Vershinin <ghettovoice@gmail.com>
@version 0.9.1
@license MIT
@copyright (c) 2017-2018, Vladimir Vershinin <ghettovoice@gmail.com>
*/
import _slicedToArray from 'babel-runtime/helpers/slicedToArray';
import _isFinite from 'lodash/fp/isFinite';
import _constant from 'lodash/fp/constant';
import _isFunction from 'lodash/fp/isFunction';
import _stubArray from 'lodash/fp/stubArray';
import _differenceWith from 'lodash/fp/differenceWith';
import VectorSource from 'ol/source/vector';
import { featureHelper, formatHelper, loadStrategyHelper, projHelper, vectorSource } from '../core';

var props = {
  /**
   * Array of GeoJSON features with coordinates in the map view projection.
   * @type {GeoJSONFeature[]} features
   */
  features: {
    type: Array,
    default: _stubArray
  },
  /**
   * Source loader factory.
   * Source loader should load features from some remote service, decode them and pas to `features` prop to render.
   * @type {(function(): ol.FeatureLoader|undefined)} loaderFactory
   */
  loaderFactory: Function,
  /**
   * Source format factory
   * @type {(function(): ol.format.Feature|undefined)} formatFactory
   */
  formatFactory: {
    type: Function,
    default: defaultFormatFactory
  },
  /**
   * String or url factory
   * @type {(string|function(): string|ol.FeatureUrlFunction|undefined)} url
   */
  url: [String, Function],
  /**
   * Loading strategy factory.
   * Extent here in map view projection.
   * @type {(function(): ol.LoadingStrategy|undefined)} strategyFactory
   */
  strategyFactory: {
    type: Function,
    default: defaultStrategyFactory
  },
  overlaps: {
    type: Boolean,
    default: true
  }
};

var computed = {
  featureIds: function featureIds() {
    return this.features.map(featureHelper.getId);
  }
};

var methods = {
  /**
   * @return {ol.source.Vector}
   * @protected
   */
  createSource: function createSource() {
    return new VectorSource({
      attributions: this.attributions,
      projection: this.projection,
      loader: this.createLoader(),
      useSpatialIndex: this.useSpatialIndex,
      wrapX: this.wrapX,
      logo: this.logo,
      strategy: this.strategyFactory(),
      format: this.formatFactory(),
      url: this.createUrlFunc(),
      overlaps: this.overlaps
    });
  },

  /**
   * @protected
   */
  createUrlFunc: function createUrlFunc() {
    var _this = this;

    if (!this.url) {
      return;
    }
    var url = this.url;
    if (!_isFunction(url)) {
      url = _constant(this.url);
    }
    // wrap strategy function to transform map view projection to source projection
    return function (extent, resolution, projection) {
      return url(transformExtent(extent, projection, _this.projection), resolution, _this.projection);
    };
  },

  /**
   * @protected
   */
  createLoader: function createLoader() {
    var _this2 = this;

    if (!this.loaderFactory) {
      return;
    }
    var loader = this.loaderFactory();
    // wrap strategy function to transform map view projection to source projection
    return function (extent, resolution, projection) {
      return loader(transformExtent(extent, projection, _this2.projection), resolution, _this2.projection);
    };
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    vectorSource.methods.mount.call(this);
    this.addFeatures(this.features);
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    this.clear();
    vectorSource.methods.unmount.call(this);
  }
};

var diffById = _differenceWith(function (a, b) {
  return a.id === b.id;
});
var watch = {
  features: function features(value, oldValue) {
    if (!this.$source) return;

    var forAdd = diffById(value, oldValue);
    var forRemove = diffById(oldValue, value);

    this.addFeatures(forAdd);
    this.removeFeatures(forRemove);
  }
};

var Source = {
  name: 'vl-source-vector',
  mixins: [vectorSource],
  props: props,
  computed: computed,
  methods: methods,
  watch: watch

  /**
   * @return {ol.LoadingStrategy}
   */
};function defaultStrategyFactory() {
  return loadStrategyHelper.all;
}

/**
 * @return {ol.format.GeoJSON}
 */
function defaultFormatFactory() {
  return formatHelper.geoJson();
}

function transformExtent(extent, sourceProj, destProj) {
  extent = extent.slice();
  if (_isFinite(extent[0]) && _isFinite(extent[1])) {
    var _projHelper$transform = projHelper.transform([extent[0], extent[1]], sourceProj, destProj);

    var _projHelper$transform2 = _slicedToArray(_projHelper$transform, 2);

    extent[0] = _projHelper$transform2[0];
    extent[1] = _projHelper$transform2[1];
  }
  if (_isFinite(extent[2]) && _isFinite(extent[3])) {
    var _projHelper$transform3 = projHelper.transform([extent[2], extent[3]], sourceProj, destProj);

    var _projHelper$transform4 = _slicedToArray(_projHelper$transform3, 2);

    extent[2] = _projHelper$transform4[0];
    extent[3] = _projHelper$transform4[1];
  }
  return extent;
}

var index = {
  Source: Source,
  install: function install(Vue) {
    Vue.component(Source.name, Source);
  }
};

export default index;

//# sourceMappingURL=index.es.js.map



//////////////////
// WEBPACK FOOTER
// ./node_modules/vuelayers/lib/vector-source/index.es.js
// module id = null
// module chunks = 